/**
 * Knowledge Map Component
 *
 * Renders an interactive Leaflet map showing regions colored by completion rate.
 * Displays merged GeoJSON from all completed quizzes.
 */

import { useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import { getCompletionColor, getCompletionOpacity } from "@/utils/knowledgeMapUtils";

interface KnowledgeMapProps {
  /** Merged GeoJSON with knowledge metadata */
  geoJSON: FeatureCollection<Geometry, GeoJsonProperties>;
  /** Called when a feature is clicked */
  onFeatureClick?: (region: string, knowledge: any) => void;
  /** Height of the map container */
  height?: string;
}

export function KnowledgeMap({
  geoJSON,
  onFeatureClick,
  height = "600px",
}: KnowledgeMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const geoJsonLayerRef = useRef<L.GeoJSON | null>(null);

  // Initialize map once
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    console.log("🗺️ Initializing knowledge map");

    const map = L.map(mapContainerRef.current, {
      center: [20, 0],
      zoom: 2,
      minZoom: 1,
      maxZoom: 10,
      zoomControl: true,
    });

    // Add base map tiles
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    mapRef.current = map;

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Style function for features (memoized to prevent recreation)
  const styleFeature = useCallback((feature: any) => {
    const knowledge = feature.properties?.__knowledge;
    const completionRate = knowledge?.completionRate || 0;
    const achievedPerfection = knowledge?.achievedPerfection || false;
    const lastUpdated = knowledge?.lastUpdated || null;

    return {
      fillColor: getCompletionColor(achievedPerfection, lastUpdated, completionRate),
      fillOpacity: getCompletionOpacity(completionRate),
      color: "#334155", // Border color
      weight: 1,
      opacity: 0.8,
    };
  }, []);

  // Handle feature interactions (memoized to prevent recreation)
  const onEachFeature = useCallback((feature: any, layer: L.Layer) => {
      const knowledge = feature.properties?.__knowledge;

      // Use the region from knowledge state (the quiz name) for consistent display
      const region = knowledge?.region ||
        feature.properties?.Country ||
        feature.properties?.Region ||
        feature.properties?.NAME ||
        "Unknown";

      // Tooltip on hover
      if (!knowledge) {
        layer.bindTooltip(`<strong>${region}</strong><br/>No data yet`, { sticky: true });
        return;
      }

      const completionRate = knowledge.completionRate || 0;
      let tooltipContent = `<strong>${region}</strong><br/>`;

      // Always show completion percentage and prompts
      tooltipContent += `${Math.round(completionRate)}% complete<br/>`;
      tooltipContent += `${knowledge.completedPrompts}/${knowledge.totalPrompts} prompts`;

      // Add freshness info for perfect scores (based on last practice)
      if (knowledge.achievedPerfection && knowledge.lastUpdated) {
        const lastPracticeDate = new Date(knowledge.lastUpdated);
        const daysSince = Math.floor((Date.now() - lastPracticeDate.getTime()) / (1000 * 60 * 60 * 24));

        tooltipContent += `<br/>`;

        if (daysSince === 0) {
          tooltipContent += `Practiced today 🔥`;
        } else if (daysSince === 1) {
          tooltipContent += `Practiced yesterday 🔥`;
        } else if (daysSince < 7) {
          tooltipContent += `Practiced ${daysSince} days ago 🔥`;
        } else if (daysSince < 14) {
          tooltipContent += `Practiced ${daysSince} days ago`;
        } else if (daysSince < 30) {
          tooltipContent += `${Math.floor(daysSince / 7)} weeks ago ⚠️`;
        } else {
          tooltipContent += `${Math.floor(daysSince / 30)} months ago 🔴`;
        }
      }

      layer.bindTooltip(tooltipContent, {
        sticky: true,
      });

      // Highlight on hover
      layer.on({
        mouseover: (e) => {
          const layer = e.target;
          layer.setStyle({
            weight: 3,
            opacity: 1,
            fillOpacity: 0.9,
          });
        },
        mouseout: (e) => {
          const layer = e.target;
          const originalStyle = styleFeature(feature);
          layer.setStyle(originalStyle);
        },
        click: () => {
          if (onFeatureClick && knowledge) {
            onFeatureClick(region, knowledge);
          }
        },
      });
    }, [onFeatureClick, styleFeature]);

  // Update GeoJSON layer when data changes
  useEffect(() => {
    if (!mapRef.current || !geoJSON) return;

    console.log(`🎨 Rendering ${geoJSON.features.length} features on knowledge map`);

    // Remove old layer
    if (geoJsonLayerRef.current) {
      geoJsonLayerRef.current.remove();
    }

    // Add new layer
    const layer = L.geoJSON(geoJSON as any, {
      style: styleFeature,
      onEachFeature: onEachFeature,
    });

    layer.addTo(mapRef.current);
    geoJsonLayerRef.current = layer;

    // Fit bounds to show all features
    if (geoJSON.features.length > 0) {
      const bounds = layer.getBounds();
      if (bounds.isValid()) {
        mapRef.current.fitBounds(bounds, {
          padding: [20, 20],
          maxZoom: 6,
        });
      }
    }
  }, [geoJSON, styleFeature, onEachFeature]);

  return (
    <div
      ref={mapContainerRef}
      style={{ height, width: "100%" }}
      className="map-container rounded-lg overflow-hidden border shadow-sm"
    />
  );
}
